import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Subscription } from "rxjs";
import { MSXReportService } from "../../services/msx_report.service";
import { SessionService } from "../../services/session.service";
import { environment } from "../../../environments/environment";

/** sessionStorage key — last MSX group we mounted Evidence for this tab. */
const BOUND_GROUP_KEY = "clio_evidence_bound_group";

/**
 * Iframe host for the Evidence.dev pilot surface served by clio-server
 * (see ClioAdmin #68 — RC2 Stage 2 build, 2026-05-27 reframe).
 *
 * Transition model (client-side today; server-side mint later):
 *   1. Resolve the effective MSX group from the SPA entity picker
 *      (`localStorage.selectedGroup`) or the login JWT's `msxInfo.groupId`.
 *   2. Wait for a session JWT before creating the iframe (no unauthenticated
 *      Evidence load / placeholder Parquet race).
 *   3. If the effective group differs from the last Evidence mount in this tab,
 *      tear down and remount so DuckDB-WASM picks up fresh L1 Parquet bytes.
 *   4. postMessage `{ type: 'clio.jwt', jwt, groupId }` after load — L1 still
 *      scopes Athena from the JWT today; `groupId` is carried for the overlay
 *      and future per-group server-minted tokens.
 */
@Component({
    selector: "app-evidence-playground",
    templateUrl: "./evidence-playground.component.html",
    styleUrls: ["./evidence-playground.component.scss"],
})
export class EvidencePlaygroundComponent implements OnInit, OnDestroy {
    @ViewChild("frame", { static: false }) frame: ElementRef<HTMLIFrameElement>;

    /** UI phase — iframe exists only during `loading` / `ready`. */
    public phase: "waiting-jwt" | "waiting-group" | "loading" | "ready" = "waiting-jwt";
    public boundGroupId = "";
    public boundGroupName = "";
    /** Mirrors iframe instrumentation once L1 Parquet + report are ready. */
    public evidenceReady = false;
    public evidenceRowCount: number | null = null;
    public evidenceSwapCount = 0;
    /** True when remounting because admin changed entity since last visit. */
    public groupContextChanged = false;

    public iframeUrl: SafeResourceUrl | null = null;

    private rawIframeUrl = "";
    private iframeOrigin = "";
    private jwtWaitAttempts = 0;
    private jwtWaitTimer: ReturnType<typeof setTimeout> | null = null;
    private groupSub: Subscription | null = null;
    private parentMessageHandler: ((ev: MessageEvent) => void) | null = null;
    private handshakeAttempts = 0;
    /** Set when the iframe sends back ANY `clio.evidence.state` message,
     *  including in-progress phases. Stops further scheduled handshake posts. */
    private iframeAcked = false;

    constructor(
        private sanitizer: DomSanitizer,
        private sessionService: SessionService,
        private msxReportService: MSXReportService
    ) {}

    ngOnInit(): void {
        this.parentMessageHandler = (ev: MessageEvent) => this.onIframeMessage(ev);
        window.addEventListener("message", this.parentMessageHandler);
        this.groupSub = this.msxReportService.groupChangeNotifier.subscribe((groupId) => {
            this.onGroupContextChanged(groupId);
        });
        this.tryMountEvidence();
    }

    ngOnDestroy(): void {
        if (this.jwtWaitTimer) {
            clearTimeout(this.jwtWaitTimer);
        }
        if (this.parentMessageHandler) {
            window.removeEventListener("message", this.parentMessageHandler);
        }
        this.groupSub?.unsubscribe();
    }

    /** Manual refresh — new cache-bust + remount for the current group. */
    public reloadEvidence(): void {
        this.evidenceReady = false;
        this.evidenceRowCount = null;
        this.evidenceSwapCount = 0;
        this.handshakeAttempts = 0;
        this.iframeAcked = false;
        this.teardownIframe();
        this.tryMountEvidence();
    }

    /**
     * Fires when the iframe document finishes loading. Posts JWT + group context
     * to the iframe targeting its exact origin (never `'*'`).
     *
     * NG0100 guard: defer the `phase` mutation to a microtask. Without this,
     * Angular detects a change to `[attr.data-phase]` after change-detection
     * has run for this turn and throws ExpressionChangedAfterItHasBeenChecked.
     */
    public onLoad(): void {
        Promise.resolve().then(() => { this.phase = "ready"; });
        this.handshakeAttempts = 0;
        this.iframeAcked = false;
        this.scheduleHandshakeRetries();
    }

    /**
     * Send the JWT handshake, with a single fallback retry. Both sends are
     * skipped as soon as the iframe ACKs back via `clio.evidence.state`
     * (any phase, including in-flight). The earlier 5-retry schedule
     * (0/100/500/1500/3000ms) sent posts before the iframe finished
     * navigating to its content origin — those rejected with the browser's
     * target-origin mismatch warning AND fed the iframe redundant JWTs
     * that re-triggered Parquet swaps. One post + one fallback is enough.
     */
    private scheduleHandshakeRetries(): void {
        for (const delayMs of [250, 2000]) {
            setTimeout(() => this.sendHandshakeToIframe(), delayMs);
        }
    }

    private onIframeMessage(ev: MessageEvent): void {
        if (!ev?.data || ev.data.type !== "clio.evidence.state") {
            return;
        }
        if (this.iframeOrigin && ev.origin !== this.iframeOrigin) {
            return;
        }
        // Any state ACK from the iframe means it received the JWT and is
        // working (loading / ready / error). Stop further handshake retries.
        this.iframeAcked = true;
        if (typeof ev.data.swapCount === "number") {
            this.evidenceSwapCount = ev.data.swapCount;
        }
        if (ev.data.phase === "ready") {
            this.evidenceReady = true;
            const rc = ev.data.rowCount;
            this.evidenceRowCount = rc === null || rc === undefined ? null : Number(rc);
        }
        if (ev.data.phase === "error") {
            this.evidenceReady = false;
        }
    }

    private onGroupContextChanged(_groupId: string): void {
        this.teardownIframe();
        this.tryMountEvidence();
    }

    /**
     * Gate Evidence mount on JWT availability and group context. Client-side
     * only for now — future server step mints iframe HTML / short-lived tokens
     * bound to { userId, groupId } before we set iframeUrl.
     */
    private tryMountEvidence(): void {
        const token = this.getJwt();
        if (!token) {
            this.phase = "waiting-jwt";
            if (this.jwtWaitAttempts < 30) {
                this.jwtWaitAttempts += 1;
                this.jwtWaitTimer = setTimeout(() => this.tryMountEvidence(), 100);
            }
            return;
        }
        this.jwtWaitAttempts = 0;

        const ctx = this.resolveGroupContext();
        if (!ctx.groupId) {
            this.phase = "waiting-group";
            return;
        }

        const normalized = this.normalizeGroupId(ctx.groupId);
        const prior = sessionStorage.getItem(BOUND_GROUP_KEY);
        this.groupContextChanged = !!(prior && prior !== normalized);

        this.evidenceReady = false;
        this.evidenceRowCount = null;
        this.evidenceSwapCount = 0;
        this.handshakeAttempts = 0;
        this.boundGroupId = ctx.groupId;
        this.boundGroupName = ctx.groupName;
        this.phase = "loading";
        this.teardownIframe();
        this.buildAndMountIframe(token, ctx, normalized);
    }

    private buildAndMountIframe(
        _token: string,
        ctx: { groupId: string; groupName: string },
        normalizedGroupId: string
    ): void {
        const base = environment.evidence_iframe_uri || "";
        const sep = base.indexOf("?") === -1 ? "?" : "&";
        // Cache-bust only — group scope travels via postMessage, not ?tenant=.
        this.rawIframeUrl = `${base}${sep}ts=${Date.now()}`;

        try {
            this.iframeOrigin = new URL(this.rawIframeUrl, window.location.href).origin;
        } catch (_) {
            this.iframeOrigin = "";
        }

        sessionStorage.setItem(BOUND_GROUP_KEY, normalizedGroupId);

        // Defer src assignment so *ngIf creates a fresh iframe element (clean
        // DuckDB-WASM state when group or cache-bust changes).
        setTimeout(() => {
            this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.rawIframeUrl);
        }, 0);
    }

    private teardownIframe(): void {
        this.iframeUrl = null;
    }

    private sendHandshakeToIframe(): void {
        if (this.evidenceReady || this.iframeAcked) {
            return;
        }
        this.handshakeAttempts += 1;
        const token = this.getJwt();
        if (!token) {
            console.warn("[evidence-playground] no JWT on session; skipping postMessage handshake");
            return;
        }
        if (!this.iframeOrigin) {
            console.warn("[evidence-playground] iframe origin unresolved; skipping postMessage handshake");
            return;
        }
        const win = this.frame?.nativeElement?.contentWindow;
        if (!win) {
            console.warn("[evidence-playground] iframe contentWindow unavailable on load");
            return;
        }
        // postMessage throws (DOMException) when the target origin doesn't
        // match the recipient window's actual origin — common during the
        // brief window where the iframe is still navigating to its content
        // origin (e.g. localhost:8000) but the browser still has it on the
        // parent origin (localhost:4200). The fallback retry covers this.
        try {
            win.postMessage(
                {
                    type: "clio.jwt",
                    jwt: token,
                    groupId: this.boundGroupId,
                },
                this.iframeOrigin
            );
        } catch (err) {
            console.warn(
                "[evidence-playground] postMessage attempt",
                this.handshakeAttempts,
                "failed (target-origin race?); will retry:",
                err
            );
        }
    }

    /**
     * Effective MSX group for Evidence — mirrors MSX report routes: admin entity
     * picker (`selectedGroup`) overrides the login JWT default when set.
     */
    private resolveGroupContext(): { groupId: string; groupName: string } {
        const user = this.sessionService.currentUser?.attributes;
        const msx = user?.msxInfo || user?.msxinfo;
        const selectedGroup = (localStorage.getItem("selectedGroup") || "").trim();
        const selectedGroupName = (localStorage.getItem("selectedGroupName") || "").trim();
        const jwtGroupId = (msx?.groupId || "").trim();
        const jwtGroupName = (msx?.groupName || "").trim();
        const groupId = selectedGroup || jwtGroupId;
        const groupName = selectedGroupName || jwtGroupName || groupId;
        return { groupId, groupName };
    }

    private getJwt(): string {
        return this.sessionService.currentUser?.attributes?.token || "";
    }

    private normalizeGroupId(id: string): string {
        return (id || "").trim().toLowerCase();
    }
}
